<script setup lang="ts">
import { Dialog as DialogElement, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { useEventListener } from "@vueuse/core";
import { differenceWith, isEqual, range } from "lodash-es";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import LeLanguagePair from "@/components/LeLanguagePair.vue";
import { DictionariesHelper, MatomoHelper } from "@/helpers";
import { type Dictionary, MatomoEventAction, MatomoEventCategory } from "@/types";

import LeDictionarySelectorItem from "./LeDictionarySelectorItem.vue";

interface Props {
  dictionaries: Dictionary[];
  dictionariesWithResults?: Dictionary[];
}

const props = withDefaults(defineProps<Props>(), {
  dictionariesWithResults: () => []
});

const emit = defineEmits<{
  select: [dictionaryId: string];
}>();

const { t } = useI18n();

const isOpen = ref<boolean>(false);
const query = ref<string>("");
const activeResultIndex = ref<number>(0);

const MAX_RESULTS = 7;

useEventListener(window, "keydown", (event) => {
  const eventHasModifier = event.metaKey || event.ctrlKey;

  // Open/close keys
  if (eventHasModifier && event.key === "k") {
    event.preventDefault();
    isOpen.value = !isOpen.value;

    if (isOpen.value) {
      query.value = "";
      activeResultIndex.value = 0;
    }
  }

  if (!isOpen.value) {
    return;
  }

  if (event.key === "Escape") {
    onClose();
  }

  // Navigation keys
  if (event.key === "ArrowDown") {
    event.preventDefault();

    if (activeResultIndex.value === lastResultIndex.value) {
      activeResultIndex.value = 0;
      return;
    }

    activeResultIndex.value += 1;
  }

  if (event.key === "ArrowUp") {
    event.preventDefault();

    if (activeResultIndex.value === 0) {
      activeResultIndex.value = lastResultIndex.value;
      return;
    }

    activeResultIndex.value -= 1;
  }

  // Selection keys
  if (event.key === "Enter") {
    onSelect(results.value[activeResultIndex.value]);
  }

  const acceptedNumberKeys = range(1, MAX_RESULTS + 1).map((i) => i.toString());

  if (eventHasModifier && acceptedNumberKeys.includes(event.key)) {
    const index = Number(event.key.slice(-1)) - 1;

    if (index <= lastResultIndex.value) {
      event.preventDefault();
      onSelect(results.value[index]);
    }
  }
});

const results = computed<Dictionary[]>(() => {
  if (!query.value) {
    return [];
  }

  return [
    ...props.dictionaries.filter((dictionary) => dictionary.id.toLowerCase() === query.value.toLowerCase()),
    ...DictionariesHelper.sortByPublisherPriority(
      props.dictionaries.filter((dictionary) => {
        const words = query.value.split(" ");
        const searchKey = `${dictionary.publisher.toLowerCase()} ${dictionary.title.toLowerCase()}`;

        return words.every((word) => searchKey.includes(word.toLowerCase()));
      })
    )
  ].slice(0, MAX_RESULTS);
});

watch(
  () => results.value,
  (newResults, oldResults) => {
    if (
      differenceWith(oldResults, newResults, isEqual).length > 0 ||
      differenceWith(newResults, oldResults, isEqual).length > 0
    ) {
      activeResultIndex.value = -1;
    }
  }
);

const activeResult = computed<Dictionary>(() => results.value[activeResultIndex.value]);

const lastResultIndex = computed<number>(() => results.value.length - 1);

const onSelect = (dictionary: Dictionary) => {
  onClose();
  emit("select", dictionary.id);

  trackSelectDictionary();
};

const onClose = () => {
  isOpen.value = false;
};

const trackOpenOmnibar = () => {
  MatomoHelper.sendEvent(MatomoEventCategory.Omnibar, MatomoEventAction.OpenOmnibar);
};

const trackSelectDictionary = () => {
  MatomoHelper.sendEvent(MatomoEventCategory.Omnibar, MatomoEventAction.PickDictionaryInOmnibar);
};

watch(isOpen, (value) => {
  if (value) {
    trackOpenOmnibar();
  }
});
</script>

<template>
  <TransitionRoot :show="isOpen" as="template">
    <DialogElement
      v-bind="$attrs"
      tabindex="-1"
      class="le-omni-dictionary-selector"
      @keydown.esc.stop.prevent="onClose()"
    >
      <TransitionChild
        as="template"
        enter="fade-enter-active"
        enter-from="fade-enter-from"
        leave="fade-leave-active"
        leave-to="fade-leave-to"
      >
        <div class="le-omni-dictionary-selector__backdrop" aria-hidden="true" @click="onClose()" />
      </TransitionChild>
      <TransitionChild
        as="template"
        enter="fade-enter-active"
        enter-from="fade-enter-from"
        leave="fade-leave-active"
        leave-to="fade-leave-to"
      >
        <div class="le-omni-dictionary-selector__dialog">
          <DialogPanel class="le-omni-dictionary-selector__panel">
            <div class="le-omni-dictionary-selector__body">
              <input
                v-model="query"
                type="text"
                role="combobox"
                class="le-omni-dictionary-selector__input"
                :placeholder="t('le-omni-dictionary-selector.placeholder')"
                aria-controls="omni-dictionaries"
                aria-autocomplete="list"
                :aria-expanded="isOpen"
                :aria-activedescendant="activeResult?.id ?? ''"
              />

              <ul
                v-if="results.length"
                id="omni-dictionaries"
                class="le-omni-dictionary-selector__results"
                role="listbox"
                :aria-label="t('le-omni-dictionary-selector.dictionaries')"
              >
                <li
                  v-for="(dictionary, index) in results"
                  :id="dictionary.id"
                  :key="dictionary.id"
                  @mouseenter="activeResultIndex = index"
                >
                  <LeDictionarySelectorItem
                    :dictionary="dictionary"
                    :has-result="props.dictionariesWithResults.includes(dictionary)"
                    :is-selected="activeResultIndex === index"
                    @select="onSelect(dictionary)"
                  >
                    <template #languages="{ searchFilter }">
                      <LeLanguagePair
                        v-if="searchFilter.sourceLanguage"
                        class="le-omni-dictionary-selector__language-pair"
                        :source-language="searchFilter.sourceLanguage"
                        :direction="searchFilter.direction"
                        :target-language="searchFilter.targetLanguage"
                      />
                    </template>
                  </LeDictionarySelectorItem>
                </li>
              </ul>
              <div v-else class="le-omni-dictionary-selector__no-results">
                {{ t("le-omni-dictionary-selector.no-results") }}
              </div>
            </div>
          </DialogPanel>
        </div>
      </TransitionChild>
    </DialogElement>
  </TransitionRoot>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

// For some reason .le-omni-dictionary-selector doesn't get get v-scope-id
:global(.le-omni-dictionary-selector) {
  display: flex;
  position: fixed;
  z-index: 1040;
  top: 60%;
  right: 0;
  bottom: 0;
  left: 0;
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
}

.le-omni-dictionary-selector {
  &__backdrop {
    position: fixed;
    z-index: 1;
    inset: 0;
    background: fade(@colors[@neutrals][300], 40%);
  }

  &__dialog {
    z-index: 2;
    width: 40%;
    margin-top: -30vh;
    box-shadow: 0px 11px 12px -7px rgba(32, 43, 33, 0.25);

    .body-text("m");
  }

  &__panel {
    max-height: 100vh;

    @supports (max-height: 100dvh) {
      max-height: 100dvh;

      #media-breakpoint-up(lg, {
        max-height: 96dvh;
      });
    }

    overflow-y: auto;
    border-radius: 0;

    #media-breakpoint-up(lg, {
      max-height: 96vh;
      border-radius: 4px;
    });
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    border-radius: 4px;
    background: @colors[@secondary][200];
  }

  &__input {
    width: 100%;
    height: 64px;
    padding: 16px;
    border: none;
    border-radius: 4px;
    background: fade(@colors[@neutrals][100], 90%);

    .body-text("l");
  }

  &__language-pair {
    align-self: flex-end;
    gap: 4px;
    line-height: 18px;
    .body-text("xs", "semi-bold", "italic");
  }

  &__results {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__no-results {
    .body-text("l");
    color: fade(@colors[@neutrals][400], 50%);
  }
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 75ms ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
